import { useEffect, useRef, useState } from "react";
import { phaseLabel, etaTargetLabel } from "@/lib/tripPhase";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  Car, User, MapPin, Navigation, Phone, Clock, 
  RefreshCw, ZoomIn, ZoomOut, Locate
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Fix for default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom markers
const createCustomIcon = (color, isActive = false) => {
  const size = isActive ? 40 : 32;
  const pulseClass = isActive ? 'animate-pulse' : '';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="relative ${pulseClass}" style="width: ${size}px; height: ${size}px;">
        <div class="absolute inset-0 rounded-full ${color} shadow-lg flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/>
            <path d="M9 17h6"/>
            <circle cx="17" cy="17" r="2"/>
          </svg>
        </div>
        ${isActive ? `<div class="absolute inset-0 rounded-full ${color} opacity-30 animate-ping"></div>` : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

const createPickupIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="w-8 h-8 bg-blue-500 rounded-full shadow-lg flex items-center justify-center border-2 border-white">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createDropoffIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="w-8 h-8 bg-red-500 rounded-full shadow-lg flex items-center justify-center border-2 border-white">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
          <line x1="4" x2="4" y1="22" y2="15"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createClientIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="w-10 h-10 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full shadow-lg flex items-center justify-center border-2 border-white animate-bounce">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

// Component to recenter map
function RecenterMap({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

// Simulated driver positions for demo (when no real data)
const generateMockDriverPositions = (baseLocation, count = 8) => {
  const drivers = [];
  const names = ["Alex Johnson", "Maria Garcia", "James Smith", "Sarah Davis", "Michael Brown", "Emily Wilson", "David Lee", "Lisa Anderson"];
  const vehicles = [
    { make: "Toyota", model: "Sienna", color: "White" },
    { make: "Honda", model: "Odyssey", color: "Silver" },
    { make: "Ford", model: "Transit", color: "Blue" },
    { make: "Dodge", model: "Grand Caravan", color: "Black" },
    { make: "Chrysler", model: "Pacifica", color: "Gray" },
    { make: "Toyota", model: "Camry", color: "White" },
    { make: "Honda", model: "Accord", color: "Silver" },
    { make: "Ford", model: "Explorer", color: "Black" }
  ];
  
  for (let i = 0; i < count; i++) {
    const offset = 0.025; // Spread drivers across a wider area
    // Ensure some drivers are available (at least 60%)
    const isAvailable = i < 5 || Math.random() > 0.5;
    const hasActiveRide = !isAvailable && Math.random() > 0.3;
    
    drivers.push({
      id: `mock-driver-${i}`,
      name: names[i] || `Driver ${i + 1}`,
      latitude: baseLocation.lat + (Math.random() - 0.5) * offset,
      longitude: baseLocation.lng + (Math.random() - 0.5) * offset,
      is_available: isAvailable,
      phone: `(555) ${300 + i}-00${10 + i}`,
      vehicle_make: vehicles[i]?.make || "Toyota",
      vehicle_model: vehicles[i]?.model || "Camry",
      vehicle_color: vehicles[i]?.color || "White",
      vehicle_plate: `${['ABC', 'XYZ', 'DEF', 'GHI', 'JKL', 'MNO', 'PQR', 'STU'][i]}-${1000 + i}`,
      active_ride: hasActiveRide ? {
        client_name: `Client ${Math.floor(Math.random() * 100)}`,
        status: ["in_progress", "arrived"][Math.floor(Math.random() * 2)],
        eta_minutes: Math.floor(Math.random() * 20) + 5
      } : null
    });
  }
  return drivers;
};

export default function LiveFleetMap({
  mode = "dispatch", // "dispatch" | "driver" | "client"
  drivers = [], // Array of driver objects with locations (for dispatch mode)
  currentRide = null, // Current ride object (for driver/client mode)
  driverLocation = null, // { lat, lng } of current driver (for driver mode)
  clientLocation = null, // { lat, lng } of client being picked up (for client mode)
  onDriverSelect = null, // Callback when a driver is clicked (dispatch mode)
  onRefresh = null, // Callback to refresh data
  height = "400px",
  variant = "light", // "light" or "dark"
  showControls = true,
  className = ""
}) {
  const mapRef = useRef(null);
  const [center, setCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default NYC
  const [zoom, setZoom] = useState(13);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [displayDrivers, setDisplayDrivers] = useState([]);

  // Initialize with mock data if no real drivers provided or add locations to drivers without them
  useEffect(() => {
    if (mode === "dispatch") {
      if (drivers.length > 0) {
        // Add mock locations to drivers that don't have current_location
        const driversWithLocations = drivers.map((driver, index) => {
          if (driver.current_location?.latitude && driver.current_location?.longitude) {
            return {
              ...driver,
              latitude: driver.current_location.latitude,
              longitude: driver.current_location.longitude,
              is_available: !driver.active_ride
            };
          } else {
            // Assign mock location for drivers without GPS data
            const offset = 0.02;
            return {
              ...driver,
              latitude: center.lat + (Math.random() - 0.5) * offset,
              longitude: center.lng + (Math.random() - 0.5) * offset,
              is_available: !driver.active_ride
            };
          }
        });
        setDisplayDrivers(driversWithLocations);
        
        // Center on first driver with real location
        const driverWithLoc = drivers.find(d => d.current_location?.latitude);
        if (driverWithLoc) {
          setCenter({
            lat: driverWithLoc.current_location.latitude,
            lng: driverWithLoc.current_location.longitude
          });
        }
      } else {
        // Generate mock drivers for demo
        setDisplayDrivers(generateMockDriverPositions(center, 8));
      }
    }
  }, [drivers, mode]);

  // Set center based on mode
  useEffect(() => {
    if (mode === "driver" && driverLocation) {
      setCenter({ lat: driverLocation.lat, lng: driverLocation.lng });
    } else if (mode === "client" && currentRide) {
      // Center between pickup and driver location
      const pickup = currentRide.pickup_locations?.[0];
      if (pickup?.latitude && pickup?.longitude) {
        setCenter({ lat: pickup.latitude, lng: pickup.longitude });
      } else if (currentRide.current_latitude && currentRide.current_longitude) {
        setCenter({ lat: currentRide.current_latitude, lng: currentRide.current_longitude });
      }
    }
  }, [mode, driverLocation, currentRide]);

  const handleRefresh = async () => {
    setIsLoading(true);
    if (onRefresh) {
      await onRefresh();
    }
    // Simulate refresh delay
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleDriverClick = (driver) => {
    setSelectedDriver(driver.id === selectedDriver ? null : driver.id);
    if (onDriverSelect) {
      onDriverSelect(driver);
    }
  };

  // Map tile based on variant
  const tileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  // Generate route line if we have pickup/dropoff
  const routePoints = [];
  const liveRoute = currentRide?.route_polyline?.length > 1 ? currentRide.route_polyline : null;
  if (currentRide && !liveRoute) {
    if (currentRide.current_latitude && currentRide.current_longitude) {
      routePoints.push([currentRide.current_latitude, currentRide.current_longitude]);
    }
    currentRide.pickup_locations?.forEach(loc => {
      if (loc.latitude && loc.longitude) {
        routePoints.push([loc.latitude, loc.longitude]);
      }
    });
    if (currentRide.dropoff_latitude && currentRide.dropoff_longitude) {
      routePoints.push([currentRide.dropoff_latitude, currentRide.dropoff_longitude]);
    }
  }

  return (
    <div 
      className={`relative rounded-xl overflow-hidden ${className}`} 
      style={{ height, zIndex: 0 }}
      data-testid="live-fleet-map"
    >
      {/* Map */}
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={tileUrl}
          className={variant === "dark" ? "map-tiles-dark" : ""}
        />
        
        <RecenterMap center={[center.lat, center.lng]} zoom={zoom} />

        {/* DISPATCH MODE: Show all drivers */}
        {mode === "dispatch" && displayDrivers.map((driver) => {
          const lat = driver.current_location?.latitude || driver.latitude;
          const lng = driver.current_location?.longitude || driver.longitude;
          if (!lat || !lng) return null;
          
          const isSelected = selectedDriver === driver.id;
          const isAvailable = driver.is_available;
          const colorClass = isAvailable ? "bg-lime-500" : "bg-amber-500";
          
          return (
            <Marker
              key={driver.id}
              position={[lat, lng]}
              icon={createCustomIcon(colorClass, driver.active_ride)}
              eventHandlers={{
                click: () => handleDriverClick(driver)
              }}
            >
              <Popup>
                <div className="min-w-[200px] p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${isAvailable ? 'bg-lime-500' : 'bg-amber-500'}`} />
                    <span className="font-bold text-slate-800">{driver.name}</span>
                  </div>
                  
                  {driver.vehicle_make && (
                    <p className="text-sm text-slate-600 mb-1">
                      {[driver.vehicle_color, driver.vehicle_make, driver.vehicle_model].filter(Boolean).join(' ')}
                    </p>
                  )}
                  
                  {driver.phone && (
                    <p className="text-sm text-slate-600 mb-2">
                      <Phone className="w-3 h-3 inline mr-1" />{driver.phone}
                    </p>
                  )}
                  
                  {driver.active_ride ? (
                    <div className="bg-amber-100 rounded-lg p-2 mt-2">
                      <p className="text-xs font-semibold text-amber-800">On Active Ride</p>
                      <p className="text-sm text-amber-700">{driver.active_ride.client_name}</p>
                      {driver.active_ride.eta_minutes && (
                        <p className="text-xs text-amber-600">ETA: {driver.active_ride.eta_minutes} min</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-lime-100 rounded-lg p-2 mt-2">
                      <p className="text-sm font-semibold text-lime-800">Available for rides</p>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* DRIVER/CLIENT MODE: Show route markers */}
        {(mode === "driver" || mode === "client") && currentRide && (
          <>
            {/* Driver position */}
            {(driverLocation || (currentRide.current_latitude && currentRide.current_longitude)) && (
              <Marker
                position={[
                  driverLocation?.lat || currentRide.current_latitude,
                  driverLocation?.lng || currentRide.current_longitude
                ]}
                icon={createCustomIcon("bg-cyan-500", true)}
              >
                <Popup>
                  <div className="p-2">
                    <p className="font-bold text-slate-800">{currentRide.driver_name || "Driver"}</p>
                    {currentRide.eta_minutes && (
                      <p className="text-sm text-cyan-600">ETA: {currentRide.eta_minutes} min</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Pickup locations */}
            {currentRide.pickup_locations?.map((loc, index) => (
              loc.latitude && loc.longitude && (
                <Marker
                  key={`pickup-${index}`}
                  position={[loc.latitude, loc.longitude]}
                  icon={createPickupIcon()}
                >
                  <Popup>
                    <div className="p-2">
                      <p className="font-bold text-blue-800">Pickup {index + 1}</p>
                      <p className="text-sm text-slate-600">{loc.address}</p>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}

            {/* Dropoff location */}
            {currentRide.dropoff_latitude && currentRide.dropoff_longitude && (
              <Marker
                position={[currentRide.dropoff_latitude, currentRide.dropoff_longitude]}
                icon={createDropoffIcon()}
              >
                <Popup>
                  <div className="p-2">
                    <p className="font-bold text-red-800">Dropoff</p>
                    <p className="text-sm text-slate-600">{currentRide.dropoff_address}</p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Client location marker (for client mode) */}
            {mode === "client" && clientLocation && (
              <Marker
                position={[clientLocation.lat, clientLocation.lng]}
                icon={createClientIcon()}
              >
                <Popup>
                  <div className="p-2">
                    <p className="font-bold text-rose-800">Your Location</p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Route polyline */}
            {routePoints.length > 1 && (
              <Polyline
                positions={routePoints}
                color={variant === "dark" ? "#22d3ee" : "#0ea5e9"}
                weight={4}
                opacity={0.8}
                dashArray="10, 10"
              />
            )}
            {liveRoute && (
              <Polyline
                positions={liveRoute}
                color={variant === "dark" ? "#a3e635" : "#0d9488"}
                weight={5}
                opacity={0.9}
              />
            )}
          </>
        )}
      </MapContainer>

      {/* Controls overlay */}
      {showControls && (
        <div className="absolute top-3 right-3 z-[30] flex flex-col gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="w-9 h-9 p-0 bg-white/90 hover:bg-white shadow-md"
            onClick={() => setZoom(z => Math.min(z + 1, 18))}
            data-testid="map-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="w-9 h-9 p-0 bg-white/90 hover:bg-white shadow-md"
            onClick={() => setZoom(z => Math.max(z - 1, 10))}
            data-testid="map-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          {onRefresh && (
            <Button
              size="sm"
              variant="secondary"
              className="w-9 h-9 p-0 bg-white/90 hover:bg-white shadow-md"
              onClick={handleRefresh}
              disabled={isLoading}
              data-testid="map-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      )}

      {/* Legend (Dispatch mode) */}
      {mode === "dispatch" && (
        <div className={`absolute bottom-3 left-3 z-[30] px-3 py-2 rounded-lg shadow-md ${
          variant === "dark" ? "bg-slate-800/90 text-white" : "bg-white/90 text-slate-800"
        }`}>
          <p className="text-xs font-semibold mb-2">Fleet Status</p>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-lime-500" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span>On Ride</span>
            </div>
          </div>
          <p className="text-xs mt-2 text-slate-500">
            {displayDrivers.filter(d => d.is_available).length} of {displayDrivers.length} available
          </p>
        </div>
      )}

      {/* Status overlay (Client mode) */}
      {mode === "client" && currentRide && (
        <div className={`absolute bottom-3 left-3 right-3 z-[30] px-4 py-3 rounded-lg shadow-lg ${
          variant === "dark" ? "bg-slate-800/95 text-white" : "bg-white/95 text-slate-800"
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {phaseLabel(currentRide)}
              </p>
              <p className="font-bold">{currentRide.driver_name}</p>
              {currentRide.driver_vehicle_make && (
                <p className="text-sm text-slate-500">
                  {[currentRide.driver_vehicle_color, currentRide.driver_vehicle_make].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
            {currentRide.eta_minutes && currentRide.status === "in_progress" && (
              <div className={`text-right px-3 py-2 rounded-lg ${
                variant === "dark" ? "bg-cyan-400/20" : "bg-cyan-100"
              }`}>
                <p className="text-xs text-cyan-600">ETA {etaTargetLabel(currentRide)}</p>
                <p className={`text-2xl font-bold ${variant === "dark" ? "text-cyan-400" : "text-cyan-700"}`}>
                  {currentRide.eta_minutes}<span className="text-sm ml-1">min</span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/30 z-[35] flex items-center justify-center">
          <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
            <span className="text-sm">Updating...</span>
          </div>
        </div>
      )}
    </div>
  );
}
